import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Shield, Lock, Eye, Trash2 } from "lucide-react";

interface PrivacyConsentProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

export const PrivacyConsent = ({ open, onAccept, onDecline }: PrivacyConsentProps) => {
  const [agreed, setAgreed] = useState(false);

  const handleAccept = () => {
    if (agreed) {
      localStorage.setItem("snapbill_privacy_consent", "true");
      onAccept();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onDecline()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-5 w-5 text-primary" />
            Privacy & Data Usage Consent
          </DialogTitle>
          <DialogDescription className="text-left space-y-4 pt-4">
            <p className="text-base">
              Before you upload photos to SnapBill, please review how we handle your data in compliance with Malaysia's Personal Data Protection Act (PDPA).
            </p>

            <div className="space-y-3">
              <div className="flex gap-3 items-start">
                <Eye className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-foreground">Photo Analysis</h4>
                  <p className="text-sm">
                    Your job site photos will be analyzed using AI to detect materials and estimate costs. Photos are processed securely and used only for invoice generation.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 items-start">
                <Lock className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-foreground">Data Storage</h4>
                  <p className="text-sm">
                    Photos and invoices are stored securely in encrypted storage. Only you can access your data. We implement industry-standard security measures including Row-Level Security and encrypted connections.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 items-start">
                <Trash2 className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-foreground">Data Retention & Your Rights</h4>
                  <p className="text-sm">
                    You have the right to access, modify, or delete your data at any time. Photos and invoices are retained for your records but can be deleted from your dashboard. Contact support to permanently delete your account and all associated data.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-muted p-4 rounded-lg mt-4">
              <h4 className="font-semibold text-foreground mb-2">We will NOT:</h4>
              <ul className="text-sm space-y-1 list-disc list-inside">
                <li>Share your photos or data with third parties</li>
                <li>Use your data for marketing without consent</li>
                <li>Sell or monetize your information</li>
                <li>Store sensitive metadata (location data is removed)</li>
              </ul>
            </div>

            <p className="text-sm text-muted-foreground italic">
              By proceeding, you acknowledge that you have read and understood how your data will be processed. You can withdraw consent at any time by contacting support.
            </p>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-col sm:flex-row gap-3">
          <div className="flex items-center space-x-2 mr-auto">
            <Checkbox
              id="privacy-agree"
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked === true)}
            />
            <Label
              htmlFor="privacy-agree"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              I understand and agree to the data usage terms
            </Label>
          </div>
          
          <Button variant="outline" onClick={onDecline}>
            Decline
          </Button>
          <Button onClick={handleAccept} disabled={!agreed}>
            Accept & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
